/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { createAgentSession, updateAgentSessionStep } from "@/modules/sessions/requests";
import {
  AgentSessionStatus,
  AgentSessionStep,
  AgentSessionStepType,
  AgentSessionType,
  MultiFileVendorCreateRequest,
  MultiFileVendorRagCreateRequestInput,
  MultiVendorAgentSession,
} from "@/modules/sessions/types";
import { getAgentData, getAgentStateActions } from "../states";

import { sendThirdPartyVendorFormEmail } from "@/modules/vendors/requests/index.ts";
import requestConfig from "@config/request";
import { AGENT_ROUTES, ROUTES } from "@/shared/constants/routes";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { addNotification } from "@/shared/states/notification";
import { userStateSelector } from "@/shared/states/user";
import { AiResponseType } from "@/shared/types/user";
import { NavigateFunction } from "react-router-dom";
import { useVendorRiskAssessmentStore } from "../components/VendorRiskAssessment/useVendorRiskAssessment/useVendorRiskAssessmentStore";
import { VENDOR_OVERVIEW_KEY_MAPPING } from "../constants/vendorAssessmentConstants.ts";
import { emitRagCreate, getRAGExcelFromJSON } from "../requests";
import { AGENT_TYPES, AgentData, AgentSourceFile, MultiVendorAssessmentTypes } from "../types/index.ts";
import { VendorRiskSectionEnum } from "../types/vendorAssessment";
import { VendorIntakeFormData } from "../types/vendorIntakeForm.ts";
import { useAgentSocket } from "../utils/agent-socket.ts";
import { AutoSaveFunctionArgs } from "../utils/autosave";
import { getFileNameForDownloadReport } from "../utils/downloadReport.ts";
import { getResponseTypes } from "../utils/get-response-type";
import { checkAndRefreshUrl } from "../utils/refreshUrl.ts";
import { handleSocketResponse } from "../utils/socket-response";
import { generateFinalVendorReviewResponseData } from "./vendor-risk.use-case";

/**
 * Formats vendor intake form validation error messages for better readability.
 * Parses error messages that list multiple validation issues and formats them
 * with proper line breaks and bullet points.
 *
 * @param error - The error object from the API response
 * @returns Formatted error message string with line breaks
 *
 * @example
 * Input: "The following required questions are not answered: Question #1 (field1), Question #2 (field2)"
 * Output:
 * "The following required questions are not answered:
 *
 *   • Question #1 (field1)
 *   • Question #2 (field2)"
 */
export const formatVendorIntakeFormError = (error: any): string => {
  const defaultMessage = error?.message || "Failed to process vendor assessment. Please try again.";

  // Check if error has the specific format with question details
  if (!error?.response?.data?.error_message) {
    return defaultMessage;
  }

  const rawMessage = error.response.data.error_message;

  // Parse the message to separate the main text from question details
  // Expected format: "Main message: Item 1, Item 2, Item 3"
  const match = rawMessage.match(/^([^:]+):\s*(.+)$/);

  if (!match) {
    return rawMessage;
  }

  const mainMessage = match[1].trim();
  const itemsPart = match[2].trim();

  // Split items by comma and format each on a new line with proper spacing
  const items = itemsPart.split(',').map((item: string) => item.trim());

  // Format with line breaks and bullet points
  return mainMessage + ':\n\n' + items.map((item: string) => `  • ${item}`).join('\n');
};

export const handleAutoSaveOfVendorIntakeForm = async (
  agentId: string,
  formData?: VendorIntakeFormData,
  signedUrls?: string[],
  markAsComplete?: boolean,
) => {
  const agentData = getAgentData<
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
  >(agentId);
  const actions = getAgentStateActions();

  if (!agentData) {
    throw new Error("An error occurred");
  }

  const { stepData, mainData } = agentData;

  const vendorProfileStep = stepData?.find((step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE);
  if (!vendorProfileStep) {
    throw new Error("An error occurred while saving");
  }

  const currentFormData = vendorProfileStep.data?.vendor_intake_form || ({} as VendorIntakeFormData);
  const currentSignedUrls = vendorProfileStep.data?.source_urls || [];

  const hasFormDataChanged = JSON.stringify(currentFormData) !== JSON.stringify(formData);
  const hasSignedUrlsChanged = JSON.stringify(currentSignedUrls) !== JSON.stringify(signedUrls);

  if (!hasFormDataChanged && !hasSignedUrlsChanged && !markAsComplete) {
    return;
  }

  const updatedStep = {
    ...vendorProfileStep,
    data: {
      ...vendorProfileStep.data,
      vendor_intake_form: formData,
      source_urls: signedUrls,
    },
    status: markAsComplete ? AgentSessionStatus.COMPLETE : AgentSessionStatus.IN_PROGRESS,
  };

  const updatedSteps = stepData.map((step) => (step.id === updatedStep.id ? updatedStep : step));

  actions.updateAgentStepData(agentId, updatedSteps as AgentSessionStep[]);

  try {
    await updateAgentSessionStep(updatedStep as AgentSessionStep);
  } catch (error) {
    addNotification({
      message: "Failed to save vendor profile. Please try again.",
      type: "error",
    });
  }
};

export const processFileForVendorAssessment = async (agentId: string, navigate: NavigateFunction) => {
  const selectedResponse =
    userStateSelector.getState().aiResponseType === AiResponseType.NONE
      ? AiResponseType.LITE
      : userStateSelector.getState().aiResponseType;
  const agentData = getAgentData<
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
  >(agentId);
  if (!agentData) {
    throw new Error("An error occurred");
  }
  let { stepData, sessionData } = agentData;
  const vendorProfileStep = stepData.find((step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE);
  const controlStep = stepData.find((step) => step.type === AgentSessionStepType.CONTROL_SELECTION);
  if (!vendorProfileStep) {
    throw new Error("Vendor profile step not found");
  }
  const sourceUrls = vendorProfileStep.data?.source_urls || [];
  const { response_mode, response_quality } = getResponseTypes(selectedResponse);

  const refreshedUrls = await Promise.all(
    sourceUrls.map(async (url) => {
      return await checkAndRefreshUrl(url);
    }),
  );

  const vendorAssessment: MultiFileVendorRagCreateRequestInput = {
    doc_type: "json",
    response_quality,
    source_urls: refreshedUrls,
    vendor_intake_form: vendorProfileStep.data?.vendor_intake_form,
    vendor_domain_url: agentData?.sessionData?.multi_file_vendor_assessment?.vendor_domain_url,
    vendor_logo_url: agentData?.sessionData?.multi_file_vendor_assessment?.vendor_logo_url,
    controls_url: (controlStep?.data && 'url' in controlStep.data) ? (controlStep.data as { url?: string }).url : undefined,
  };

  if (sessionData?.customize) {
    const updatedStepData = stepData.map((step) => {
      if (step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
        return {
          ...step,
          status: AgentSessionStatus.COMPLETE,
        };
      }
      if (step.type === AgentSessionStepType.CONTROL_SELECTION) {
        return {
          ...step,
          status: AgentSessionStatus.INPUT_NEEDED,
        };
      }
      return step;
    });

    // Find the step from updatedStepData, not original stepData
    const assessorIntakeForm = updatedStepData.find(
      (step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE
    );

    const { updateAgentStepData, setAgentData } = getAgentStateActions();

    // Update local state first to trigger immediate re-render
    updateAgentStepData(agentId, updatedStepData);
    setAgentData(agentId, {
      ...agentData,
      stepData: updatedStepData,
      sessionData
    });

    if (assessorIntakeForm) {
      updateAgentSessionStep(assessorIntakeForm as AgentSessionStep);
    }

    navigate({
      pathname: `/agent/${agentId}/`,
    });
  }

  if (!sessionData?.customize) {
    if (sessionData?.multi_file_vendor_assessment?.vendor_filled_intake_form) {
      try {
        await sendThirdPartyVendorFormEmail({
          agent_session_id: agentId,
          vendor_email: sessionData.multi_file_vendor_assessment.vendor_email as string,
          contact_email: sessionData.multi_file_vendor_assessment.contact_email as string,
          vendor_name: sessionData.multi_file_vendor_assessment.vendor_name,
          ui_redirect_url: `${requestConfig.exchangeBaseUrl}/zania-exchange-hub`,
          request_type: "initial_request",
        });

        // Update the MULTI_VENDOR_PROFILE step (0th step) status to COMPLETE
        const updatedStepData = stepData.map((step, index) => {
          if (index === 0 && step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
            return {
              ...step,
              status: AgentSessionStatus.COMPLETE,
            };
          }
          return step;
        });

        const { updateAgentSessionData, updateAgentStepData } = getAgentStateActions();

        // Update session status to FORM_SENT
        updateAgentSessionData(agentId, { status: AgentSessionStatus.FORM_SENT });

        // Update step data with the completed step
        updateAgentStepData(agentId, updatedStepData);

        updateAgentSessionStep(updatedStepData.find((step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) as AgentSessionStep);

        navigate({
          pathname: `/agent/${agentId}/`,
        });
      } catch (error) {
        // Format and show vendor intake form validation errors
        const errorMessage = formatVendorIntakeFormError(error);

        addNotification({
          message: errorMessage,
          type: "error",
          title: "Validation Error",
        });

        // Re-throw the error so the calling component knows the operation failed
        throw error;
      }
    } else {
      const multiFileVendorAssessmentStep = stepData.find(
        (step): step is AgentSessionStep =>
          (step.type as AgentSessionStepType) === AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT,
      );

      const agent_session_step_id = multiFileVendorAssessmentStep?.id ?? "";


      emitRagCreate(
        {
          agent_session_id: agentId,
          agent_session_step_id,
          rag_input: vendorAssessment,
          response_mode,
          task: "multi_file_vendor_assessment",
          vendor_intake_form: vendorProfileStep.data?.vendor_intake_form,
        },
        async (response: any, error: any) => {
          // Handle error case
          if (error || !response) {
            const errorMessage = formatVendorIntakeFormError(error);

            addNotification({
              message: errorMessage,
              type: "error",
              title: "Validation Error",
            });
            return;
          }

          // Update step statuses only on success
          stepData = stepData.map((step) => {
            if (
              [
                AgentSessionStepType.LOAD_TEMPLATE,
                AgentSessionStepType.EXTRACT_CONTROLS,
                AgentSessionStepType.MULTI_VENDOR_PROFILE,
                AgentSessionStepType.CONTROL_SELECTION,
              ].includes(step.type)
            ) {
              step.status = AgentSessionStatus.COMPLETE;
            }

            const inprogresStep = AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT; // Always set assessment step as in progress

            if ((step.type as AgentSessionStepType) === inprogresStep) {
              step.status = AgentSessionStatus.IN_PROGRESS;
            }
            return step;
          });

          const { setAgentData } = getAgentStateActions();
          setAgentData(agentId, {
            ...agentData,
            stepData,
          });

          handleSocketResponse(response, navigate);

          navigate({
            pathname: `/agent/${agentId}/`,
          });
        },
      );
    }
  }


};

export const handleAutoSaveOfVendorAssessmentResponse = async ({
  agentId,
  markAsComplete,
  data,
}: AutoSaveFunctionArgs): Promise<void> => {
  const reviewResponseData = generateFinalVendorReviewResponseData(agentId);

  const agentData = getAgentData<
    AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
  >(agentId);

  if (!agentData) {
    throw new Error("An error occurred");
  }
  const { stepData } = agentData;
  const { staleUrl, selectedId } = agentData.mainData;

  const lastUpdate = agentData.mainData.lastUpdate;
  if (lastUpdate?.isRemoteUpdate && Date.now() - lastUpdate.timestamp < 2000) {
    return;
  }

  const { setStaleUrl, updateAgentStepData } = getAgentStateActions();

  const editStepData = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

  if (!editStepData) {
    throw new Error("An occurred while saving");
  }

  const stepUrl = editStepData?.data?.url;

  let currentStaleUrl = staleUrl || stepUrl;
  let markAsCompleteDone = false;

  if (!currentStaleUrl.includes("signed_urls")) {
    const signedUrl = await getSignedUrl({
      file_names: ["updated_third_party_risks.json"],
      max_age: 86400,
    });
    const updatedStep = {
      ...editStepData,
      data: {
        url: signedUrl[0],
      },
      status: markAsComplete ? AgentSessionStatus.COMPLETE : AgentSessionStatus.INPUT_NEEDED,
    };
    const updatedSteps = stepData.map((step) => {
      if (step.id === updatedStep.id) {
        return updatedStep;
      }
      return step;
    });
    updateAgentStepData(agentId, updatedSteps);
    await updateAgentSessionStep(updatedStep);
    currentStaleUrl = signedUrl[0];
    markAsCompleteDone = true;
    setStaleUrl(agentId, currentStaleUrl);
  }

  const expiryDate = new URL(currentStaleUrl).searchParams.get("se");
  if (expiryDate) {
    const expiry = new Date(expiryDate);
    const currentTime = new Date();
    const diff = expiry.getTime() - currentTime.getTime();
    if (diff < 0) {
      const signedUrl = await getSignedUrl({
        stale_urls: [currentStaleUrl],
        max_age: 86400,
      });
      currentStaleUrl = signedUrl[0];
      setStaleUrl(agentId, currentStaleUrl);
    }
  }

  let control: any;
  const { selectedTab } = useVendorRiskAssessmentStore.getState();

  if (selectedTab === "OVERVIEW" || selectedId === "company_info" || selectedId === "product_service_info") {
    control = useVendorRiskAssessmentStore
      .getState()
      .vendorOverview.find(
        (item) => item.key === VENDOR_OVERVIEW_KEY_MAPPING[selectedId as keyof typeof VENDOR_OVERVIEW_KEY_MAPPING],
      );
  } else if (selectedTab === "SUPPLY_CHAIN") {
    control = useVendorRiskAssessmentStore.getState().vendorSupplyChainData.find((item) => item.key === selectedId);

    if (control && control.section === VendorRiskSectionEnum.vendor_supply_chain) {
      const controlIdentifier = control?.key;
      const editedField = agentData?.mainData.lastEditedField;

      const { emitControlEdit } = useAgentSocket();
      await emitControlEdit(currentStaleUrl, control, controlIdentifier, agentId, editStepData.id, editedField ?? "");

      return;
    }
  } else if (useVendorRiskAssessmentStore.getState().selectedTab === "COMPANY") {
    control = useVendorRiskAssessmentStore.getState().vendorCompanyData.find((item) => item.key === selectedId);
  } else if (selectedTab === "POTENTIAL_ISSUES") {
    const potentialIssuesData = useVendorRiskAssessmentStore.getState().vendorPotentialIssuesData;

    control = potentialIssuesData.find((item) => item.key === ("potential_issue" as any));
  } else {
    control = reviewResponseData.find((item) => item.id === selectedId);
  }

  const controlIdentifier = control?.section === "vendor_overview" ? control.key : control?.id || selectedId;
  if (!controlIdentifier) {
    return;
  }
  const editedField = agentData?.mainData.lastEditedField;

  // Track this as a local update before emitting socket event
  // This helps prevent cursor jumps when our own update echoes back
  const { setAgentData } = getAgentStateActions();
  setAgentData(agentId, {
    ...agentData,
    mainData: {
      ...agentData.mainData,
      lastUpdate: {
        timestamp: Date.now(),
        isRemoteUpdate: false
      }
    }
  });

  const { emitControlEdit } = useAgentSocket();
  await emitControlEdit(currentStaleUrl, control, controlIdentifier, agentId, editStepData.id, editedField ?? "");
};

export const processFileForVendorAssessmentResponse = async (
  url: string,
  sessionId: string,
  markAsComplete?: boolean,
) => {
  const currentData = getAgentData<AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT, never>(sessionId);

  if (!currentData) {
    throw new Error("An error occurred");
  }

  // Check if create_new_control_set is enabled
  const isCreateNewControlSet =
    (currentData.sessionData as any)?.multi_file_vendor_assessment?.create_new_control_set === true;

  const stepData = currentData.stepData.map((step) => {
    // Create new step objects to ensure proper immutability
    if (step.type === AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT) {
      const isReassessment = step.status === AgentSessionStatus.UNDER_REASSESSMENT;
      const newStatus = isReassessment ? AgentSessionStatus.UNDER_REASSESSMENT : AgentSessionStatus.COMPLETE;

      return {
        ...step,
        status: markAsComplete ? AgentSessionStatus.COMPLETE : newStatus,
      };
    }

    // If create_new_control_set is true, skip the control selection step
    if (isCreateNewControlSet && step.type === AgentSessionStepType.CONTROL_SELECTION) {
      return {
        ...step,
        status: AgentSessionStatus.COMPLETE,
      };
    }

    if (step.type === AgentSessionStepType.EDIT_RESPONSE) {
      return {
        ...step,
        // If create_new_control_set is true, set to INPUT_NEEDED to show response screen
        status: isCreateNewControlSet ? AgentSessionStatus.INPUT_NEEDED : AgentSessionStatus.COMPLETE,
        data: { url },
      };
    }
    return step;
  });

  const { updateAgentStepData, updateAgentSessionData, setAgentData } = getAgentStateActions();

  // Update step data to trigger re-render
  updateAgentStepData(sessionId, stepData);

  // Update session status from FORM_SENT to INPUT_NEEDED so it shows the response screen
  if ((currentData.sessionData as any)?.status === AgentSessionStatus.FORM_SENT) {
    updateAgentSessionData(sessionId, { status: AgentSessionStatus.INPUT_NEEDED });
  }

  // Refetch agent session data from the server to get the latest metadata (including file list)
  try {
    const { getAgentSession } = await import('@/modules/sessions/requests');
    const agentFromApi = await getAgentSession(sessionId);

    if (agentFromApi?.data) {
      // Update the agent data with fresh data from server, preserving local state
      const refreshedAgentData = {
        ...currentData,
        stepData: agentFromApi.data.steps,
        sessionData: agentFromApi.data.session,
      };
      setAgentData(sessionId, refreshedAgentData);
    }
  } catch (error) {
    console.error('Failed to refetch agent session data:', error);
    // Don't throw error to avoid breaking the flow, just log it
  }
};

export const getVendorJsonFromApi = async (agentId: string, fileFormat?: string) => {
  try {
    const agentData = getAgentData<
      AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
      MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
    >(agentId);

    if (!agentData) {
      throw new Error("An error occurred");
    }

    const { responseQuality, sessionData, stepData } = agentData;

    addNotification({
      type: "success",
      title: "Report Download in Progress...",
      message: "If the download does not begin within 15 seconds, please click the 'Download' button",
    });

    const data = await getRAGExcelFromJSON({
      task: "multi_file_vendor_assessment",
      output_format: fileFormat || "pdf",
      response_quality: responseQuality ?? AiResponseType.LITE,
      agent_session_id: agentId,
    });

    const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();

    setFinalFileUrl(agentId, data.data.file_url);

    const link = document.createElement("a");
    link.href = data.data.file_url;
    link.download = getFileNameForDownloadReport(sessionData);
    link.click();

    const downloadStepData = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);
    const editResponseStepData = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

    const updatedStepData = stepData.map((step) => {
      if (step.id === editResponseStepData?.id) {
        return {
          ...step,
          status: AgentSessionStatus.COMPLETE,
        };
      }
      if (step.id === downloadStepData?.id) {
        return {
          ...step,
          data: {
            url: data.data.file_url,
          },
        };
      }
      return step;
    });

    if (downloadStepData?.id) {
      await updateAgentSessionStep({
        ...downloadStepData,
        data: {
          url: data.data.file_url,
        },
      });
    }

    updateAgentStepData(agentId, updatedStepData as AgentSessionStep[]);

    return data.data.file_url;
  } catch (error) {
    addNotification({
      type: "error",
      message: "Error in downloading the file. Please try again.",
      title: "Error",
    });
    throw error;
  }
};

interface ProcessDataForVendorAssessmentArgs {
  name: string;
  use_training_data: boolean;
  customize: boolean;
  multi_file_vendor_assessment: {
    vendor_name: string;
    vendor_domain_url?: string;
    vendor_logo_url?: string;
    vendor_filled_intake_form: boolean;
    questionnaire_id?: string;
    product_name: string;
    scope_of_integration: string;
    vendor_email?: string;
    contact_email?: string;
    response_quality: "accuracy" | "lite" | "speed";
    doc_type: "json";
    selected_control_sets?: string[];
    create_new_control_set?: boolean;
  };
  sourceFiles: AgentSourceFile[];
  navigate: NavigateFunction;
}

export const processDataForVendorAssessment = async ({
  name,
  navigate,
  customize,
  multi_file_vendor_assessment,
}: ProcessDataForVendorAssessmentArgs) => {
  const selectedResponse =
    userStateSelector.getState().aiResponseType === AiResponseType.NONE
      ? AiResponseType.LITE
      : userStateSelector.getState().aiResponseType;

  const { response_mode, response_quality } = getResponseTypes(selectedResponse);

  const vendorAssessment: MultiFileVendorCreateRequest = {
    doc_type: "json",
    response_quality,
    source_urls: [],
    vendor_name: multi_file_vendor_assessment.vendor_name,
    vendor_domain_url: multi_file_vendor_assessment.vendor_domain_url,
    vendor_logo_url: multi_file_vendor_assessment.vendor_logo_url,
    scope_of_integration: multi_file_vendor_assessment.scope_of_integration,
    product_name: multi_file_vendor_assessment.product_name,
    vendor_filled_intake_form: multi_file_vendor_assessment.vendor_filled_intake_form,
    questionnaire_id: multi_file_vendor_assessment.questionnaire_id,
    vendor_email: multi_file_vendor_assessment.vendor_email,
    contact_email: multi_file_vendor_assessment.contact_email,
    // Add new fields for control sets
    selected_control_sets: multi_file_vendor_assessment.selected_control_sets || [],
    create_new_control_set: multi_file_vendor_assessment.create_new_control_set || false,
  };

  const {
    data: { session, steps = [] },
  } = await createAgentSession({
    name: name ?? "Vendor Assessment",
    type: AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT,
    customize,
    [AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT]: vendorAssessment,
  });

  if (!session || !session.id) {
    throw new Error("An error occurred");
  }

  const stepData = steps.reduce((acc, step) => {
    if (step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
      step.status = AgentSessionStatus.IN_PROGRESS;
    }
    acc.push(step);
    return acc;
  }, [] as AgentSessionStep[]);

  const agentData: AgentData<AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT, MultiVendorAssessmentTypes.VENDOR_ASSESSMENT> = {
    agentType: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    sessionData: session as MultiVendorAgentSession,
    stepData,
    subType: MultiVendorAssessmentTypes.VENDOR_ASSESSMENT,
    responseQuality: selectedResponse,
    mainData: {
      sourceFilesUrls: new Map(),
      approvedIds: [],
      editedIds: [],
      vendorName: multi_file_vendor_assessment.vendor_name,
      scopeOfIntegration: multi_file_vendor_assessment.scope_of_integration,
    },
  };

  const { setAgentData } = getAgentStateActions();
  setAgentData(session.id, agentData);

  navigate({
    pathname: `/agent/${session.id}/`,
  });

  // Return the session ID
  return session.id;
};

export const handleEndVendorAssessment = async (sessionId: string, navigate: NavigateFunction) => {
  const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();
  try {
    const agentData = getAgentData<
      AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
      MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
    >(sessionId);

    if (!agentData) {
      throw new Error("An error occurred");
    }

    const { stepData } = agentData;

    const editResponseStepData = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);
    const prepareReportStep = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);

    if (!editResponseStepData) {
      throw new Error("An error occurred");
    }

    if (editResponseStepData.status !== AgentSessionStatus.COMPLETE || !prepareReportStep?.data?.url) {
      const signedUrl = await getSignedUrl({
        file_names: ["updated_risks.json"],
        max_age: 86400,
      });
      const editResponseStep = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);
      await processFileForVendorAssessmentResponse(signedUrl[0], sessionId);

      const data = await getRAGExcelFromJSON({
        task: "multi_file_vendor_assessment",
        output_format: "pdf",
        response_quality: agentData.responseQuality ?? AiResponseType.LITE,
        agent_session_id: sessionId,
      });
      setFinalFileUrl(sessionId, data.data.file_url);

      const downloadStepData = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);
      const updatedStepData = stepData.map((step) => {
        if (step.id === editResponseStepData?.id) {
          return {
            ...step,
            status: AgentSessionStatus.COMPLETE,
            data: {
              url: signedUrl[0],
            },
          };
        }
        if (step.id === downloadStepData?.id) {
          return {
            ...step,
            data: {
              url: data.data.file_url,
            },
            status: AgentSessionStatus.COMPLETE,
          };
        }
        return step;
      });
      if (downloadStepData?.id) {
        await updateAgentSessionStep({
          ...downloadStepData,
          data: {
            url: data.data.file_url,
          },
          status: AgentSessionStatus.COMPLETE,
        });
      }
      updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
      const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
      navigate(successPath);
    } else {
      const updatedStepData = stepData.map((step) => {
        if (step.id === prepareReportStep?.id) {
          return { ...step, status: AgentSessionStatus.COMPLETE };
        }
        return step;
      });
      if (prepareReportStep?.id) {
        await updateAgentSessionStep({
          ...prepareReportStep,
          status: AgentSessionStatus.COMPLETE,
        });
      }
      await new Promise<void>((resolve) => {
        updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
        setTimeout(resolve, 0);
      });
      const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
      navigate(successPath);
    }
  } catch (error) {
    addNotification({
      type: "error",
      message: "Error in ending the session",
      title: "Error",
    });
  }
};

export const processFileForVendorReassessment = async ({
  name,
  sourceFiles,
  navigate,
  reassessOption,
  agentDataCurrent,
}: {
  name: string;
  sourceFiles: AgentSourceFile[];
  navigate: NavigateFunction;
  reassessOption?: string;
  agentDataCurrent?: AgentData<AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT, MultiVendorAssessmentTypes.VENDOR_ASSESSMENT>;
}) => {
  const selectedResponse =
    userStateSelector.getState().aiResponseType === AiResponseType.NONE
      ? AiResponseType.LITE
      : userStateSelector.getState().aiResponseType;

  const source_urls = sourceFiles.map((file) => file.url);
  const { response_mode, response_quality } = getResponseTypes(selectedResponse);

  const vendorProfileStep = agentDataCurrent?.stepData.find(
    (step) => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE,
  );
  const controlStep = agentDataCurrent?.stepData.find((step) => step.type === AgentSessionStepType.CONTROL_SELECTION);

  const vendorAssessment: MultiFileVendorRagCreateRequestInput = {
    doc_type: "json",
    response_quality,
    source_urls,
    vendor_intake_form: vendorProfileStep?.data?.vendor_intake_form,
    vendor_domain_url: agentDataCurrent?.sessionData?.multi_file_vendor_assessment?.vendor_domain_url,
    vendor_logo_url: agentDataCurrent?.sessionData?.multi_file_vendor_assessment?.vendor_logo_url,
    controls_url: (controlStep?.data && 'url' in controlStep.data) ? (controlStep.data as { url?: string }).url : undefined,
    assessment_type: "reassessment",
    reassessment: {
      retained: [],
    },
  };

  const session = agentDataCurrent?.sessionData;
  let stepData = agentDataCurrent?.stepData;

  if (!session || !session.id) {
    throw new Error("An error occurred");
  }

  stepData = stepData?.map((step) => {
    if (
      [
        AgentSessionStepType.LOAD_TEMPLATE,
        AgentSessionStepType.EXTRACT_CONTROLS,
        AgentSessionStepType.MULTI_VENDOR_PROFILE,
      ].includes(step.type)
    ) {
      step.status = AgentSessionStatus.COMPLETE;
    }

    if (step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
      if (step.data.vendor_intake_form) {
        step.data.vendor_intake_form.vendor_submitted_files = sourceFiles.map((file) => file.fileName);
      }
    }

    if (reassessOption) {
      if (step.type === AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT) {
        step.status = AgentSessionStatus.UNDER_REASSESSMENT;
      }
    } else {
      if (step.type === AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT) {
        step.status = AgentSessionStatus.IN_PROGRESS;
      }
    }
    return step;
  });

  const multiFileVendorAssessmentStep = stepData?.find(
    (step): step is AgentSessionStep => step.type === AgentSessionStepType.MULTI_FILE_VENDOR_ASSESSMENT,
  );

  const multiVendorProfileStep = stepData?.find(
    (step): step is AgentSessionStep => step.type === AgentSessionStepType.MULTI_VENDOR_PROFILE,
  );

  if (multiVendorProfileStep && multiVendorProfileStep.type === AgentSessionStepType.MULTI_VENDOR_PROFILE) {
    if (multiVendorProfileStep.data.vendor_intake_form) {
      multiVendorProfileStep.data.vendor_intake_form.vendor_submitted_files = sourceFiles.map((file) => file.fileName);
    }

    await updateAgentSessionStep(multiVendorProfileStep);
  }

  const agent_session_step_id = multiFileVendorAssessmentStep?.id ?? "";

  const { setAgentData } = getAgentStateActions();
  setAgentData(session.id, {
    agentType: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    sessionData: session,
    stepData: stepData || [],
    subType: MultiVendorAssessmentTypes.VENDOR_ASSESSMENT,
    responseQuality: selectedResponse,
    mainData: {
      sourceFilesUrls: new Map(sourceFiles.map((file) => [file.fileName, file])),
      approvedIds: agentDataCurrent?.mainData.approvedIds || [],
      editedIds: agentDataCurrent?.mainData.editedIds || [],
      vendorName: session.multi_file_vendor_assessment.vendor_name,
      scopeOfIntegration: session.multi_file_vendor_assessment.scope_of_integration,
    },
  });

  emitRagCreate(
    {
      agent_session_id: session.id,
      agent_session_step_id,
      rag_input: vendorAssessment,
      response_mode,
      task: "multi_file_vendor_assessment",
      vendor_intake_form: vendorProfileStep?.data?.vendor_intake_form,
    },
    (response: any, error: any) => {
      // Handle error case
      if (error || !response) {
        addNotification({
          message: error?.message || "Failed to process vendor reassessment. Please try again.",
          type: "error",
        });
        return;
      }

      handleSocketResponse(response, navigate);
      navigate({
        pathname: `/agent/${session.id}/`,
      });
    },
  );
};
